
import { supabase } from "@/integrations/supabase/client";

export interface BraceletActivity {
  referral_code: string;
  registered: boolean;
  user_id?: string;
  user_name?: string;
  scan_count: number;
  journal_count: number;
  prayer_count: number;
}

export async function fetchBraceletCodes(churchCode: string): Promise<string[]> {
  try {
    console.log("Fetching bracelet codes for church code:", churchCode);
    
    const { data, error } = await supabase
      .from("bracelets")
      .select("referral_code")
      .eq("church_code", churchCode);
      
    if (error) throw error;
    
    if (churchCode === "001") {
      const testCodes = ['TEST001', 'TEST002', 'TEST003'];
      let allCodes = data?.map(b => b.referral_code) || [];
      
      testCodes.forEach(code => {
        if (!allCodes.includes(code)) {
          allCodes.push(code);
        }
      });
      
      return allCodes;
    } else {
      return (data || []).map(b => b.referral_code);
    }
  } catch (error: any) {
    console.error("Error fetching bracelet codes:", error);
    
    if (churchCode === "001") {
      return ['TEST001', 'TEST002', 'TEST003'];
    }
    
    return [];
  }
}

async function ensureTestBraceletsExist() {
  const testCodes = ['TEST001', 'TEST002', 'TEST003'];
  
  for (const code of testCodes) {
    const { data: existingBracelet } = await supabase
      .from("bracelets")
      .select("*")
      .eq("referral_code", code)
      .maybeSingle();
      
    if (!existingBracelet) {
      console.log(`Creating test bracelet ${code} in database`);
      await supabase
        .from("bracelets")
        .insert({
          referral_code: code,
          church_code: "001",
          created_at: new Date().toISOString()
        });
    } else if (existingBracelet.church_code !== "001") {
      // Ensure the church code is set to 001 for test bracelets
      console.log(`Updating test bracelet ${code} to have church code 001`);
      await supabase
        .from("bracelets")
        .update({ church_code: "001" })
        .eq("referral_code", code);
    }
  }
}

function createTestBracelets(): BraceletActivity[] {
  return [
    {
      referral_code: 'TEST001',
      registered: false,
      scan_count: 0,
      journal_count: 0,
      prayer_count: 0
    },
    {
      referral_code: 'TEST002',
      registered: false,
      scan_count: 0,
      journal_count: 0,
      prayer_count: 0
    },
    {
      referral_code: 'TEST003',
      registered: false,
      scan_count: 0,
      journal_count: 0,
      prayer_count: 0
    }
  ];
}

export async function fetchBraceletActivityData(churchCode: string): Promise<BraceletActivity[]> {
  try {
    console.log("Fetching bracelet activity for church code:", churchCode);
    
    // First, ensure TEST bracelets exist for TEST COMM/001 church code
    if (churchCode === "001") {
      await ensureTestBraceletsExist();
    }
    
    const { data: bracelets, error: braceletError } = await supabase
      .from("bracelets")
      .select("*")
      .eq("church_code", churchCode);
      
    if (braceletError) {
      console.error("Error fetching bracelets:", braceletError);
      throw braceletError;
    }
    
    if (!bracelets || bracelets.length === 0) {
      console.log("No bracelets found for church code:", churchCode);
    } else {
      console.log("Found bracelets:", bracelets);
    }
    
    let processedBracelets = [...(bracelets || [])];
    
    // Special handling for TEST COMM / 001 church code
    if (churchCode === "001") {
      // Ensure all test bracelets are included
      const testCodes = ['TEST001', 'TEST002', 'TEST003'];
      const existingCodes = new Set(processedBracelets.map(b => b.referral_code));
      
      for (const code of testCodes) {
        if (!existingCodes.has(code)) {
          console.log(`Adding missing test bracelet ${code} to results array`);
          processedBracelets.push({
            id: -Math.floor(Math.random() * 1000), // Negative ID to indicate virtual entry
            referral_code: code,
            church_code: "001",
            created_at: new Date().toISOString(),
            registered_user_id: null,
            created_by_user_id: null,
            registered_at: null,
            is_donated: false,
            nickname: null,
            donation_type: null
          });
        }
      }
    }
    
    // Now check for any scans that might exist without corresponding bracelets
    const { data: scanData, error: scanError } = await supabase
      .from("bracelet_scans")
      .select("bracelet_id, church_code")
      .eq("church_code", churchCode);
      
    if (scanError) {
      console.error("Error fetching scan data:", scanError);
      throw scanError;
    }
    
    console.log(`Found ${scanData?.length || 0} total scans for church ${churchCode}`);
    
    // Check for bracelet IDs in scans that don't exist in the bracelets table
    const allScannedBraceletIds = [...new Set(scanData?.map(scan => scan.bracelet_id) || [])];
    const braceletIdsInDb = new Set(processedBracelets.map(b => b.referral_code));
    
    const missingBraceletIds = allScannedBraceletIds.filter(id => !braceletIdsInDb.has(id));
    
    // Add these to our bracelets array for display
    for (const braceletId of missingBraceletIds) {
      console.log(`Adding bracelet ${braceletId} that has scans but no bracelet record`);
      processedBracelets.push({
        id: -Math.floor(Math.random() * 1000), // Negative ID to indicate virtual entry
        referral_code: braceletId,
        church_code: churchCode,
        created_at: new Date().toISOString(),
        registered_user_id: null,
        created_by_user_id: null,
        registered_at: null,
        is_donated: false,
        nickname: null,
        donation_type: null
      });
    }
    
    // Count scans per bracelet
    const braceletScans = {};
    scanData?.forEach(scan => {
      braceletScans[scan.bracelet_id] = (braceletScans[scan.bracelet_id] || 0) + 1;
    });
    
    // Process all bracelets to get activity data
    const activityPromises = processedBracelets.map(async bracelet => {
      let userName = "Not registered";
      let scanCount = braceletScans[bracelet.referral_code] || 0;
      let journalCount = 0;
      let prayerCount = 0;
      
      if (bracelet.registered_user_id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", bracelet.registered_user_id)
          .single();
          
        if (profile) {
          userName = profile.full_name;
        }
        
        const { count: journalCountResult } = await supabase
          .from("journal_entries")
          .select("*", { count: "exact", head: true })
          .eq("user_id", bracelet.registered_user_id);
          
        if (journalCountResult !== null) {
          journalCount = journalCountResult;
        }
        
        const { count: prayerCountResult } = await supabase
          .from("prayer_requests")
          .select("*", { count: "exact", head: true })
          .eq("user_id", bracelet.registered_user_id);
          
        if (prayerCountResult !== null) {
          prayerCount = prayerCountResult;
        }
      }
      
      return {
        referral_code: bracelet.referral_code,
        registered: !!bracelet.registered_user_id,
        user_id: bracelet.registered_user_id || undefined,
        user_name: bracelet.registered_user_id ? userName : undefined,
        scan_count: scanCount,
        journal_count: journalCount,
        prayer_count: prayerCount
      };
    });
    
    const activityData = await Promise.all(activityPromises);
    console.log("Bracelet activity data:", activityData);
    return activityData;
    
  } catch (error) {
    console.error("Error fetching bracelet activity:", error);
    if (churchCode === "001") {
      // Fallback for TEST COMM
      return createTestBracelets();
    }
    return [];
  }
}
